import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateTestMessages } from '../../messages/services/MessagesService';

/**
 * FNP-1521: generate test messages through the API's own capability.
 *
 * ⚠️ THIS USED TO LOOP `createMessage` CLIENT-SIDE. That version was replaced
 * rather than extended, because it was broken in three ways that all shared one
 * cause — generation was not a capability of the API, so the client had to
 * imitate one:
 *
 *  - it sent no `aliasId`, and `createMessage` refuses a user-pool caller who
 *    does not own the target alias (FNP-1552), so every call threw;
 *  - nothing marked the rows as generated, so fixture data would have been
 *    indistinguishable from real mail;
 *  - N rows meant N round trips, each its own partial failure.
 *
 * The server now owns shape, marking and alias resolution. The client picks the
 * dials and calls once.
 */
export interface GenerateMessagesOptions {
    /** How many to write. The API clamps this to 1..50. */
    count: number;
    /** File them under a specific alias. Omitted: the caller's first alias. */
    aliasId?: string;
    /** Pin every message to one status. Omitted: varied. */
    status?: string;
    /** Force attachments on or off. Omitted: varied. */
    withAttachments?: boolean;
}

export const useGenerateMessage = () => {
    const queryClient = useQueryClient();

    const generateMutation = useMutation({
        mutationFn: (options: GenerateMessagesOptions) => generateTestMessages(options),
        onSuccess: () => {
            // The whole point of the button is that the list fills in front of the
            // operator. Without this the rows are written and nothing visibly
            // happens, which reads as a broken button.
            queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
    });

    const generate = async (options: GenerateMessagesOptions) =>
        generateMutation.mutateAsync(options);

    return {
        generate,
        generating: generateMutation.isPending
    };
};
